import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DeployTab.css';

// Human-friendly labels for the backend's classified ErrorCode strings.
// Unknown codes fall back to showing the raw code, so this list need not be exhaustive.
const ERROR_CODE_LABELS = {
  SDM_NAME_TAKEN: 'Semantic model name already taken',
  SDM_FIELD_NOT_MAPPED: 'Semantic model field not mapped',
  SDM_NOT_FOUND: 'Semantic model not found',
  DMO_NOT_FOUND: 'Data Model Object not found',
  DLO_NOT_FOUND: 'Data Lake Object not found',
  FEATURE_NOT_ENABLED: 'Feature not enabled in target org',
  DATASPACE_MISMATCH: 'Data space mismatch',
  CYCLIC_RELATIONSHIP: 'Cyclic relationship detected',
  VALUE_TOO_LARGE: 'Value too large',
  FORMULA_BAD_REF: 'Formula references a missing field',
  DEPLOY_VALIDATION_FAILED: 'Deployment validation failed',
  DEPLOY_EXECUTION_FAILED: 'Deployment execution failed',
  DASHBOARD_NO_PERMISSIONS: 'Insufficient dashboard permissions',
};

// Defensively parse the job.error field from a failed deploy. The backend now
// encodes a classified error object as a JSON string, but legacy/edge paths may
// still return a plain string -- fall back to using the raw string as the message.
const parseDeployError = (rawError) => {
  if (rawError == null) {
    return { message: 'Deployment failed' };
  }
  if (typeof rawError === 'object') {
    // Already an object (some callers may pass it parsed).
    return rawError;
  }
  if (typeof rawError === 'string') {
    try {
      const parsed = JSON.parse(rawError);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
      return { message: rawError };
    } catch (e) {
      return { message: rawError };
    }
  }
  return { message: String(rawError) };
};

function DeployTab() {
  const [packageFile, setPackageFile] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [semanticModels, setSemanticModels] = useState([]);
  
  const [workspaceChoice, setWorkspaceChoice] = useState('create');
  const [newWorkspaceLabel, setNewWorkspaceLabel] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  
  const [sdmChoice, setSdmChoice] = useState('create');
  const [newSdmApiName, setNewSdmApiName] = useState('');
  const [selectedSdm, setSelectedSdm] = useState('');
  const [sdmNameValid, setSdmNameValid] = useState(false);
  const [sdmNameErrors, setSdmNameErrors] = useState([]);
  const [isValidatingSdmName, setIsValidatingSdmName] = useState(false);
  
  const [validationResults, setValidationResults] = useState(null);
  const [availableFields, setAvailableFields] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [dependencyMap, setDependencyMap] = useState({});
  
  // Pre-deployment validation state
  const [preValidationResults, setPreValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationPassed, setValidationPassed] = useState(false);

  // Explicit, deliberate opt-out from validation. Default = validate-on (skip = false).
  const [skipValidation, setSkipValidation] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Structured/classified error from a failed deploy (parsed from job.error JSON string).
  const [deployError, setDeployError] = useState(null);
  const [deploymentStatus, setDeploymentStatus] = useState('not_started');
  const [deploymentSteps, setDeploymentSteps] = useState([]);
  const [workspaceUrl, setWorkspaceUrl] = useState('');
  const [pollIntervalId, setPollIntervalId] = useState(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setPackageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setPackageData(data);
          setError(null);
        } catch (err) {
          setError('Could not parse package.json file');
          setPackageFile(null);
          setPackageData(null);
        }
      };
      reader.readAsText(file);
    } else {
      setError('Please upload a valid JSON file');
    }
  };

  const fetchWorkspaces = async () => {
    if (workspaces.length > 0) return;
    try {
      const response = await axios.get('/api/v1/deployment/workspaces');
      setWorkspaces(response.data.workspaces || []);
    } catch (err) {
      console.error('Error fetching workspaces:', err);
    }
  };

  const fetchSemanticModels = async () => {
    if (semanticModels.length > 0) return;
    try {
      const response = await axios.get('/api/v1/deployment/semantic-models');
      setSemanticModels(response.data.semantic_models || []);
    } catch (err) {
      console.error('Error fetching semantic models:', err);
    }
  };

  useEffect(() => {
    if (workspaceChoice === 'existing') {
      fetchWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceChoice]);

  useEffect(() => {
    if (sdmChoice === 'existing') {
      fetchSemanticModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdmChoice]);

  const validateSdmName = async (name) => {
    if (!name) {
      setSdmNameValid(false);
      setSdmNameErrors([]);
      setIsValidatingSdmName(false);
      return;
    }

    setIsValidatingSdmName(true);
    try {
      const response = await axios.post(`/api/v1/deployment/validate-sdm-name?sdm_name=${encodeURIComponent(name)}`);
      setSdmNameValid(response.data.valid);
      setSdmNameErrors(response.data.errors || []);
    } catch (err) {
      setSdmNameValid(false);
      setSdmNameErrors(['Failed to validate name']);
    } finally {
      setIsValidatingSdmName(false);
    }
  };

  useEffect(() => {
    if (sdmChoice === 'create' && newSdmApiName) {
      // Reset validation state when name changes (before starting new validation)
      setSdmNameValid(false);
      setSdmNameErrors([]);
      
      const timer = setTimeout(() => {
        validateSdmName(newSdmApiName);
      }, 500);
      return () => clearTimeout(timer);
    } else if (sdmChoice === 'create' && !newSdmApiName) {
      // Reset when name is cleared
      setSdmNameValid(false);
      setSdmNameErrors([]);
      setIsValidatingSdmName(false);
    }
  }, [newSdmApiName, sdmChoice]);

  const validateRequirements = async () => {
    if (!selectedSdm || !packageData) return;

    setLoading(true);
    try {
      const response = await axios.post('/api/v1/deployment/validate-requirements', {
        sdm_api_name: selectedSdm,
        package_data: packageData
      });
      
      if (response.data.valid) {
        setValidationResults(null);
        setDependencyMap({});
      } else {
        setValidationResults(response.data.missing);
        setAvailableFields(response.data.available);
        setTemplateDetails(response.data.template_details);
      }
    } catch (err) {
      setError('Failed to validate requirements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sdmChoice === 'existing' && selectedSdm && packageData) {
      validateRequirements();
    } else {
      setValidationResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSdm, packageData, sdmChoice]);

  const runPreDeploymentValidation = async () => {
    if (!packageData) return;
    
    setIsValidating(true);
    setError(null);
    
    const workspaceApiName = workspaceChoice === 'create' ? newWorkspaceLabel : selectedWorkspace;
    const workspaceLabel = workspaceChoice === 'create' ? newWorkspaceLabel : 
      workspaces.find(w => w.name === selectedWorkspace)?.label || selectedWorkspace;
    const sdmApiName = sdmChoice === 'create' ? newSdmApiName : selectedSdm;
    
    try {
      // Start async validation
      const response = await axios.post('/api/v1/deployment/validate-async', {
        package_data: packageData,
        workspace_choice: workspaceChoice,
        workspace_api_name: workspaceApiName,
        workspace_label: workspaceLabel,
        sdm_choice: sdmChoice,
        sdm_api_name: sdmApiName,
        dependency_map: Object.keys(dependencyMap).length > 0 ? dependencyMap : null
      });
      
      const jobId = response.data.job_id;
      console.log('Validation job started:', jobId);
      
      // Poll for validation results
      const pollValidation = async () => {
        try {
          const statusResponse = await axios.get(`/api/v1/deployment/validate/status/${jobId}`);
          const job = statusResponse.data;
          
          console.log('Validation status:', job.status);
          
          if (job.status === 'completed') {
            setPreValidationResults({ 
              errors: job.errors || [], 
              warnings: job.warnings || [] 
            });
            setValidationPassed(job.valid);
            setIsValidating(false);
            return true; // Stop polling
          } else if (job.status === 'failed') {
            setError('Validation failed: ' + (job.error || 'Unknown error'));
            setPreValidationResults(null);
            setValidationPassed(false);
            setIsValidating(false);
            return true; // Stop polling
          }
          
          return false; // Continue polling
        } catch (err) {
          setError('Failed to get validation status: ' + err.message);
          setIsValidating(false);
          return true; // Stop polling on error
        }
      };
      
      // Start polling every 2 seconds
      const interval = setInterval(async () => {
        const shouldStop = await pollValidation();
        if (shouldStop) {
          clearInterval(interval);
        }
      }, 2000);
      
      // Initial poll
      await pollValidation();
      
    } catch (err) {
      setError('Validation failed: ' + (err.response?.data?.detail || err.message));
      setPreValidationResults(null);
      setValidationPassed(false);
      setIsValidating(false);
    }
  };

  // Reset validation when configuration changes (but don't auto-trigger)
  // Don't reset if SDM name validation is in progress to avoid race conditions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Skip reset if SDM validation is in progress to prevent flickering
    if (isValidatingSdmName) {
      return;
    }
    
    setPreValidationResults(null);
    setValidationPassed(false);
    
    // Reset SDM name validation state when name changes (if not validating)
    if (sdmChoice === 'create' && newSdmApiName) {
      // sdmNameValid will be reset by the validation useEffect, so we don't need to reset it here
      // to avoid double-reset
    }
  }, [
    workspaceChoice,
    newWorkspaceLabel,
    selectedWorkspace,
    sdmChoice,
    newSdmApiName,
    selectedSdm,
    dependencyMap
    // Removed isValidatingSdmName from dependencies - reset should only run on config changes
  ]);

  const isValidationReady = () => {
    // Check if form is ready for validation - requires SDM name validation to complete successfully
    // Note: Validation doesn't require workspace - it validates package against org, not workspace
    if (!packageData) return false;

    // For create SDM: require validation to complete successfully (not just filled, not in progress)
    if (sdmChoice === 'create') {
      return sdmNameValid && newSdmApiName && !isValidatingSdmName;
    } else {
      // For existing SDM: just check if SDM is selected (no async validation needed)
      return !!selectedSdm;
    }
  };

  const isDeployReady = () => {
    // Stricter check for deployment - requires SDM name validation
    if (!packageData) return false;
    
    const workspaceReady = workspaceChoice === 'create' ? newWorkspaceLabel : selectedWorkspace;
    if (!workspaceReady) return false;

    if (sdmChoice === 'create') {
      return sdmNameValid && newSdmApiName;
    } else {
      if (!selectedSdm) return false;
      if (!validationResults) return true; // No missing dependencies
      
      const totalMissing = Object.values(validationResults).reduce((sum, arr) => sum + arr.length, 0);
      const validMappings = Object.keys(dependencyMap).filter(k => dependencyMap[k] !== '').length;
      return totalMissing === validMappings;
    }
  };

  const pollDeploymentStatus = async (jobId) => {
    try {
      const response = await axios.get(`/api/v1/deployment/deploy/status/${jobId}`);
      const jobData = response.data;
      
      console.log('Polling status:', jobData.status, 'Steps:', jobData.steps?.length || 0, 'Error:', jobData.error ? 'Present' : 'None');
      
      // Update steps if available
      if (jobData.steps && jobData.steps.length > 0) {
        setDeploymentSteps(jobData.steps);
      }
      
      // Defensive check: if error field exists, treat as failure even if status isn't "failed"
      if (jobData.error) {
        console.log('Deployment failed (error field detected)!', jobData);
        const parsed = parseDeployError(jobData.error);
        setDeployError(parsed);
        setDeploymentStatus('error');
        setError(parsed.message || 'Deployment failed');
        setLoading(false);
        return true; // Stop polling
      }

      // Check job status
      if (jobData.status === 'completed') {
        // Double-check: completed status should not have errors
        if (jobData.error) {
          console.warn('Warning: Job marked completed but has error field', jobData);
          const parsed = parseDeployError(jobData.error);
          setDeployError(parsed);
          setDeploymentStatus('error');
          setError(parsed.message || 'Deployment completed with errors');
          setLoading(false);
          return true;
        }
        console.log('Deployment completed!', jobData);
        setDeploymentStatus('complete');
        setWorkspaceUrl(jobData.workspace_url || '');
        setLoading(false);
        return true; // Stop polling
      } else if (jobData.status === 'failed') {
        console.log('Deployment failed!', jobData);
        const parsed = parseDeployError(jobData.error);
        setDeployError(parsed);
        setDeploymentStatus('error');
        setError(parsed.message || 'Deployment failed');
        setLoading(false);
        return true; // Stop polling
      }
      
      // Continue polling if still running or pending
      console.log('Continuing to poll... status:', jobData.status);
      return false;
    } catch (err) {
      console.error('Error polling deployment status:', err);
      setDeploymentStatus('error');
      setError('Failed to get deployment status');
      setLoading(false);
      return true; // Stop polling on error
    }
  };

  const handleDeploy = async () => {
    setLoading(true);
    setError(null);
    setDeployError(null);
    setDeploymentStatus('running');
    setDeploymentSteps([]);

    const workspaceApiName = workspaceChoice === 'create' ? newWorkspaceLabel : selectedWorkspace;
    const workspaceLabel = workspaceChoice === 'create' ? newWorkspaceLabel : 
      workspaces.find(w => w.name === selectedWorkspace)?.label || selectedWorkspace;
    
    const sdmApiName = sdmChoice === 'create' ? newSdmApiName : selectedSdm;

    try {
      // Increase timeout for initial request to 90 seconds
      const response = await axios.post('/api/v1/deployment/deploy', {
        package_data: packageData,
        workspace_choice: workspaceChoice,
        workspace_api_name: workspaceApiName,
        workspace_label: workspaceLabel,
        sdm_choice: sdmChoice,
        sdm_api_name: sdmApiName,
        dependency_map: Object.keys(dependencyMap).length > 0 ? dependencyMap : null,
        skip_validation: skipValidation  // Deliberate opt-out from the explicit "Skip validation (advanced)" control
      }, {
        timeout: 90000 // 90 seconds timeout
      });

      // Store job ID and start polling
      const jobId = response.data.job_id;
      console.log('Deployment job started:', jobId);
      
      // Wait 1 second before starting to poll to let background task initialize
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Poll every 2 seconds
      const interval = setInterval(async () => {
        const shouldStop = await pollDeploymentStatus(jobId);
        if (shouldStop) {
          clearInterval(interval);
          setPollIntervalId(null);
        }
      }, 2000);
      
      setPollIntervalId(interval);
      
      // Initial poll
      await pollDeploymentStatus(jobId);
      
    } catch (err) {
      setDeploymentStatus('error');
      setError(err.response?.data?.detail || err.message || 'Deployment failed to start');
      setLoading(false);
    }
  };

  const resetDeployment = () => {
    // Clear polling interval if active
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      setPollIntervalId(null);
    }
    
    setDeploymentStatus('not_started');
    setDeploymentSteps([]);
    setError(null);
    setDeployError(null);
    setPackageFile(null);
    setPackageData(null);
    setDependencyMap({});
    setValidationResults(null);
    setPreValidationResults(null);
    setValidationPassed(false);
    setSkipValidation(false); // Reset back to validate-on (safe default) for the next deployment
  };

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
    };
  }, [pollIntervalId]);

  return (
    <div>
      <h2>Package Deployment</h2>
      <div className="alert-warning">
        <strong>⚠️ Important:</strong> Before starting, log out and log back into the <strong>target org</strong> where you want to deploy the dashboard.
      </div>

      <div className="container-bordered">
        <h3>1. Upload Your Package File</h3>
        <div className="file-upload-area">
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            id="file-upload"
            style={{ display: 'none' }}
          />
          <label htmlFor="file-upload" className="file-uploader">
            {packageFile ? (
              <div>
                <strong>✓ File uploaded:</strong> {packageFile.name}
              </div>
            ) : (
              <div>
                <strong>Click to upload</strong> your _package.json file
                <br />
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  This is the single JSON file generated by the 'Package Dashboard' tab
                </span>
              </div>
            )}
          </label>
        </div>

        {packageData && (
          <div className="alert-success">
            Successfully loaded package for dashboard: <strong>{packageData.templateId}</strong>
          </div>
        )}
      </div>

      <div className="container-bordered">
        <h3>2. Configure Your Workspace</h3>
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              value="create"
              checked={workspaceChoice === 'create'}
              onChange={(e) => setWorkspaceChoice(e.target.value)}
            />
            Create a new workspace
          </label>
          <label className="radio-option">
            <input
              type="radio"
              value="existing"
              checked={workspaceChoice === 'existing'}
              onChange={(e) => setWorkspaceChoice(e.target.value)}
            />
            Use an existing workspace
          </label>
        </div>

        {workspaceChoice === 'create' ? (
          <div style={{ marginTop: '1rem' }}>
            <label htmlFor="new-workspace" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              New Workspace Name
            </label>
            <input
              id="new-workspace"
              type="text"
              className="input-field"
              placeholder="My New Workspace"
              value={newWorkspaceLabel}
              onChange={(e) => setNewWorkspaceLabel(e.target.value)}
            />
          </div>
        ) : (
          <div style={{ marginTop: '1rem' }}>
            <label htmlFor="existing-workspace" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Select an existing workspace
            </label>
            <select
              id="existing-workspace"
              className="select-field"
              value={selectedWorkspace}
              onChange={(e) => setSelectedWorkspace(e.target.value)}
            >
              <option value="">-- Choose a workspace --</option>
              {workspaces.map((w) => (
                <option key={w.name} value={w.name}>
                  {w.label} ({w.name})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="container-bordered">
        <h3>3. Configure Your Semantic Model</h3>
        <div className="alert-warning">
          <strong>IMPORTANT: Data Cloud Prerequisites</strong>
          <br /><br />
          This application <strong>DOES NOT</strong> migrate the underlying Data Cloud infrastructure (Data Streams, DLOs, DMOs). 
          Before deploying, you <strong>MUST</strong> ensure the required Data Model Objects (DMOs) already exist in the target org, 
          typically by deploying a <strong>Data Kit</strong> first.{' '}
          <a 
            href="https://help.salesforce.com/s/articleView?id=sf.c360_a_data_package_kits.htm&language=en_US&type=5"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
          >
            Learn more about Data Kits here.
          </a>
        </div>

        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              value="create"
              checked={sdmChoice === 'create'}
              onChange={(e) => setSdmChoice(e.target.value)}
            />
            Create a new semantic model from template
          </label>
          <label className="radio-option">
            <input
              type="radio"
              value="existing"
              checked={sdmChoice === 'existing'}
              onChange={(e) => setSdmChoice(e.target.value)}
            />
            Use an existing semantic model
          </label>
        </div>

        {sdmChoice === 'create' ? (
          <div style={{ marginTop: '1rem' }}>
            <label htmlFor="new-sdm" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              New Semantic Model API Name
            </label>
            <input
              id="new-sdm"
              type="text"
              className="input-field"
              placeholder="MyNewSemanticModel"
              value={newSdmApiName}
              onChange={(e) => setNewSdmApiName(e.target.value)}
            />
            {sdmNameErrors.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {sdmNameErrors.map((err, idx) => (
                  <div key={idx} className="alert-error" style={{ padding: '0.5rem', marginTop: '0.25rem' }}>
                    {err}
                  </div>
                ))}
              </div>
            )}
            {sdmNameValid && newSdmApiName && (
              <div className="alert-success" style={{ marginTop: '0.5rem', padding: '0.5rem' }}>
                The API name '{newSdmApiName}' is valid and available!
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: '1rem' }}>
            <label htmlFor="existing-sdm" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Select an existing semantic model
            </label>
            <select
              id="existing-sdm"
              className="select-field"
              value={selectedSdm}
              onChange={(e) => setSelectedSdm(e.target.value)}
            >
              <option value="">-- Choose a semantic model --</option>
              {semanticModels.map((s) => (
                <option key={s.apiName} value={s.apiName}>
                  {s.label} ({s.apiName})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {validationResults && Object.keys(validationResults).length > 0 && (
        <div className="container-bordered">
          <h3>4. Requirements Validation</h3>
          <div className="alert-info">
            💡 This step checks if the selected semantic model has all the fields required by the dashboard. 
            If not, you must map the missing items to available fields.
            <br /><br />
            <strong>Mapping Feature (BETA)</strong>
            <br />
            Please be aware of the following rules when mapping fields:
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              <li>You can only map fields of the same type (e.g., Calculated Dimension to Calculated Dimension, DMO Field to DMO Field).</li>
              <li>The data types must also match (e.g., Text to Text, Number to Number).</li>
            </ul>
          </div>

          <div className="alert-warning">
            Some dependencies are missing. Please map them below.
          </div>

          {Object.entries(validationResults).map(([depType, missingItems]) => (
            <div key={depType} className="expander">
              <div className="expander-header">
                Missing {depType}s ({missingItems.length})
              </div>
              <div className="expander-content">
                {missingItems.map((item) => {
                  const itemDetails = templateDetails?.[item] || { label: item, type: 'Unknown' };
                  const optionsDict = availableFields?.[itemDetails.type] || availableFields?.['DMO Field'] || {};
                  const options = ['', ...Object.keys(optionsDict).sort()];

                  return (
                    <div key={item} className="mapping-row">
                      <div className="mapping-required">
                        <div className="alert-error" style={{ padding: '0.75rem' }}>
                          <strong>Required:</strong> <code>{item}</code>
                          <br />
                          <em>({itemDetails.type}) {itemDetails.label}</em>
                        </div>
                      </div>
                      <div className="mapping-select">
                        <label htmlFor={`map-${item}`} style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                          Map to available field:
                        </label>
                        <select
                          id={`map-${item}`}
                          className="select-field"
                          value={dependencyMap[item] || ''}
                          onChange={(e) => setDependencyMap({ ...dependencyMap, [item]: e.target.value })}
                        >
                          <option value="">-- Select a field to map --</option>
                          {options.filter(o => o !== '').map((apiName) => (
                            <option key={apiName} value={apiName}>
                              {optionsDict[apiName]} ({apiName})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pre-Deployment Validation - Always show when form is ready */}
      {isValidationReady() && deploymentStatus === 'not_started' && (
        <div className="container-bordered">
          <h3>4. Pre-Deployment Validation</h3>
          
          {/* Info message - show when not validating and no results yet */}
          {!isValidating && !preValidationResults && (
            <div className="alert-info">
              💡 Validate your package against the target org to check for missing DMOs, DLOs, and field issues before deploying.
            </div>
          )}
          
          {/* Validation in progress */}
          {isValidating && (
            <div className="alert-info">
              <div className="spinner-small"></div>
              Validating package against target org... Please wait.
            </div>
          )}
          
          {/* Validation passed */}
          {!isValidating && preValidationResults && validationPassed && preValidationResults.warnings.length === 0 && (
            <div className="alert-success">
              All validation checks passed! Package is ready to deploy.
            </div>
          )}
          
          {/* Validation passed with warnings */}
          {!isValidating && preValidationResults && validationPassed && preValidationResults.warnings.length > 0 && (
            <div className="alert-warning">
              ⚠️ Validation passed with {preValidationResults.warnings.length} warning(s).
              Review warnings below. Deployment is allowed but may have issues.
            </div>
          )}
          
          {/* Validation failed */}
          {!isValidating && preValidationResults && !validationPassed && (
            <div className="alert-error">
              ❌ Validation failed with {preValidationResults.errors.length} error(s).
              Fix the errors below before deploying.
            </div>
          )}
          
          {/* Show errors */}
          {!isValidating && preValidationResults && preValidationResults.errors.length > 0 && (
            <div className="validation-errors">
              <h4>Blocking Errors</h4>
              {preValidationResults.errors.map((error, idx) => (
                <div key={idx} className="validation-error-item">
                  <div className="error-message">{error.message}</div>
                  {error.fix_steps && error.fix_steps.length > 0 && (
                    <div className="fix-steps">
                      <strong>How to fix:</strong>
                      <ol>
                        {error.fix_steps.map((step, stepIdx) => (
                          <li key={stepIdx}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Show warnings */}
          {!isValidating && preValidationResults && preValidationResults.warnings.length > 0 && (
            <div className="validation-warnings">
              <h4>Warnings</h4>
              {preValidationResults.warnings.map((warning, idx) => (
                <div key={idx} className="validation-warning-item">
                  <div className="warning-message">{warning.message}</div>
                  {warning.fix_steps && warning.fix_steps.length > 0 && (
                    <div className="fix-steps">
                      <strong>Recommended actions:</strong>
                      <ol>
                        {warning.fix_steps.map((step, stepIdx) => (
                          <li key={stepIdx}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Validation button - always show when ready, different text based on state */}
          {!isValidating && (
            <button
              className={preValidationResults ? "button-secondary" : "button-primary"}
              onClick={() => {
                if (preValidationResults) {
                  // Re-validate: clear previous results first
                  setPreValidationResults(null);
                  setValidationPassed(false);
                }
                runPreDeploymentValidation();
              }}
              style={{ marginTop: preValidationResults ? '1rem' : '0' }}
              disabled={skipValidation}
            >
              {preValidationResults ? 'Re-validate Package' : 'Validate Package'}
            </button>
          )}

          {/* Explicit, deliberate opt-out from validation. Default = unchecked (validate-on). */}
          {!isValidating && (
            <div className="skip-validation-control">
              <label className="skip-validation-option">
                <input
                  type="checkbox"
                  checked={skipValidation}
                  onChange={(e) => setSkipValidation(e.target.checked)}
                />
                <span>Skip validation (advanced)</span>
              </label>
              <p className="skip-validation-hint">
                Validation is on by default and catches missing DMOs/DLOs, unmapped fields, and other
                blocking issues before deploying. Only skip if you know the target org is already
                prepared &mdash; if a skipped deploy fails, we&rsquo;ll show you everything validation
                would have caught.
              </p>
            </div>
          )}
        </div>
      )}

      {isDeployReady() && (validationPassed || skipValidation) && deploymentStatus === 'not_started' && (
        <>
          <hr className="divider" />
          <div className="container-bordered">
            <h3>5. Deploy Your Dashboard</h3>
            <div className="alert-info">
              Ready to deploy using <strong>{sdmChoice === 'create' ? newSdmApiName : selectedSdm}</strong> in workspace{' '}
              <strong>{workspaceChoice === 'create' ? newWorkspaceLabel : selectedWorkspace}</strong>.
            </div>
            {skipValidation && !validationPassed && (
              <div className="alert-warning">
                ⚠️ Validation is being skipped for this deploy. If it fails, we&rsquo;ll show you
                everything validation would have caught.
              </div>
            )}
            <button
              className="button-primary"
              onClick={handleDeploy}
              disabled={loading}
            >
              {loading ? 'Deploying...' : 'Deploy Dashboard'}
            </button>
          </div>
        </>
      )}

      {/* Show message if validation failed (unless the user has explicitly opted to skip validation) */}
      {isValidationReady() && !validationPassed && !skipValidation && preValidationResults && preValidationResults.errors.length > 0 && deploymentStatus === 'not_started' && (
        <div className="alert-error">
          <strong>Cannot deploy:</strong> Fix validation errors above, or enable &ldquo;Skip validation (advanced)&rdquo; to deploy anyway.
        </div>
      )}

      {loading && deploymentStatus === 'running' && (
        <div className="deployment-progress">
          <div className="spinner"></div>
          <h3 style={{ marginTop: '1rem' }}>Deploying assets...</h3>
          {deploymentSteps.map((step, idx) => (
            <div key={idx} className="deployment-step">
              ✓ {step}
            </div>
          ))}
        </div>
      )}

      {deploymentStatus === 'complete' && (
        <div className="container-bordered">
          <div className="alert-success">
            <h3>All assets deployed successfully!</h3>
          </div>
          {deploymentSteps.map((step, idx) => (
            <div key={idx} className="deployment-step-complete">
              ✓ {step}
            </div>
          ))}
          <div className="two-columns" style={{ marginTop: '2rem' }}>
            <button className="button-primary" onClick={resetDeployment}>
              Start New Deployment
            </button>
            {workspaceUrl && (
              <a href={workspaceUrl} className="button-secondary" target="_blank" rel="noopener noreferrer">
                Go to Workspace
              </a>
            )}
          </div>
        </div>
      )}

      {deploymentStatus === 'error' && (() => {
        // Prefer the structured/classified error; fall back to the raw string in `error`.
        const errObj = deployError || { message: error };
        const code = errObj.code;
        const codeLabel = code ? (ERROR_CODE_LABELS[code] || code) : null;
        const message = errObj.message || error || 'Deployment failed';
        const postHoc = errObj.post_hoc_validation;

        // fix_steps may live at error.fix_steps (gate path puts them top-level) or
        // under the first blocking error in error.details.errors[*].fix_steps.
        const detailErrors = (errObj.details && Array.isArray(errObj.details.errors))
          ? errObj.details.errors
          : [];
        const topFixSteps = Array.isArray(errObj.fix_steps) ? errObj.fix_steps : [];
        const detailFixSteps = detailErrors.find(e => Array.isArray(e.fix_steps) && e.fix_steps.length > 0)?.fix_steps || [];
        const fixSteps = topFixSteps.length > 0 ? topFixSteps : detailFixSteps;

        return (
          <div className="container-bordered">
            <div className="alert-error">
              <h3>An error occurred during deployment:</h3>
              {codeLabel && (
                <div className="error-code-badge">
                  <span className="error-code-label">{codeLabel}</span>
                  {code && codeLabel !== code && (
                    <code className="error-code-raw">{code}</code>
                  )}
                </div>
              )}
              <p>{message}</p>
            </div>

            {/* Blocking errors from the validation gate (each may carry its own fix steps). */}
            {detailErrors.length > 0 && (
              <div className="validation-errors">
                <h4>Blocking Errors ({detailErrors.length})</h4>
                {detailErrors.map((blockingError, idx) => (
                  <div key={idx} className="validation-error-item">
                    <div className="error-message">{blockingError.message}</div>
                    {Array.isArray(blockingError.fix_steps) && blockingError.fix_steps.length > 0 && (
                      <div className="fix-steps">
                        <strong>How to fix:</strong>
                        <ol>
                          {blockingError.fix_steps.map((step, stepIdx) => (
                            <li key={stepIdx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Top-level / fallback fix steps when no per-error steps were rendered above. */}
            {detailErrors.length === 0 && fixSteps.length > 0 && (
              <div className="fix-steps">
                <strong>How to fix:</strong>
                <ol>
                  {fixSteps.map((step, stepIdx) => (
                    <li key={stepIdx}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Feature 7: validation was skipped and the deploy then failed -- show the full
                "would have caught this plus N other issues" message and the complete issue list. */}
            {postHoc && (
              <div className="post-hoc-validation">
                <div className="alert-warning">
                  <strong>Validation was skipped.</strong>
                  <p style={{ marginTop: '0.5rem' }}>
                    {postHoc.message || `Validation would have caught ${postHoc.issue_count || 0} issue(s) before deploying.`}
                  </p>
                </div>
                {Array.isArray(postHoc.issues) && postHoc.issues.length > 0 && (
                  <div className="validation-errors">
                    <h4>What validation would have caught ({postHoc.issue_count ?? postHoc.issues.length})</h4>
                    {postHoc.issues.map((issue, idx) => (
                      <div key={idx} className="validation-error-item">
                        <div className="error-message">{issue.message}</div>
                        {Array.isArray(issue.fix_steps) && issue.fix_steps.length > 0 && (
                          <div className="fix-steps">
                            <strong>How to fix:</strong>
                            <ol>
                              {issue.fix_steps.map((step, stepIdx) => (
                                <li key={stepIdx}>{step}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button className="button-primary" onClick={resetDeployment} style={{ marginTop: '1rem' }}>
              Start New Deployment
            </button>
          </div>
        );
      })()}
    </div>
  );
}

export default DeployTab;

